/**
 * `UcpPlatformKeyPanel` — the "Google access key" disclosure in the UCP
 * checkout row (ADR-0215 §11).
 *
 * Google's checkout signs in to a store's UCP endpoint with an access key
 * the merchant creates here and pastes into Merchant Center. The key is
 * shown once (`SecretShownOnce`), never again; Settings shows only its
 * last four characters. Creating, replacing and revoking are explicit
 * clicks (replace and revoke confirm first) — nothing saves on its own.
 */

import { useCallback, useState } from 'react';

import { useOptionalHttp } from '../../api/http-context.js';
import { createSitePlatformKey, revokeSitePlatformKey, type UcpPlatformKey } from '../../api/site-ucp.js';
import { useToast } from '../../app/providers.js';
import { ConfirmDialog } from '../../components/atoms/index.js';
import { useC } from '../../lib/theme.js';
import { SecretShownOnce } from './SecretShownOnce.js';

const sansFont = "'Geist', sans-serif";

function shortDate(iso: string | null): string {
  if (iso === null) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function UcpPlatformKeyPanel({
  site_id,
  initial,
  onChange,
}: {
  readonly site_id: string;
  readonly initial: UcpPlatformKey;
  readonly onChange?: (k: UcpPlatformKey) => void;
}): JSX.Element {
  const C = useC();
  const http = useOptionalHttp();
  const toast = useToast();
  const [status, setStatus] = useState<UcpPlatformKey>(initial);
  const [fresh, setFresh] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<'replace' | 'revoke' | null>(null);

  const update = useCallback((k: UcpPlatformKey) => {
    setStatus(k);
    onChange?.(k);
  }, [onChange]);

  const create = useCallback(async () => {
    if (http === null) return;
    setBusy(true);
    const r = await createSitePlatformKey(http, site_id);
    setBusy(false);
    if (r.kind === 'ok') {
      setFresh(r.key);
      update(r.platform_key);
    } else {
      toast({ tone: 'coral', title: 'Could not create the key.', ...(r.kind === 'error' ? { body: r.message } : {}) });
    }
  }, [http, site_id, toast, update]);

  const revoke = useCallback(async () => {
    if (http === null) return;
    setBusy(true);
    const r = await revokeSitePlatformKey(http, site_id);
    setBusy(false);
    if (r.kind === 'ok') {
      setFresh(null);
      update(r.platform_key);
      toast({ tone: 'lime', title: 'Access key revoked.', body: 'Requests that use it are refused from now on.' });
    } else {
      toast({ tone: 'coral', title: 'Could not revoke the key.', ...(r.kind === 'error' ? { body: r.message } : {}) });
    }
  }, [http, site_id, toast, update]);

  const hosts = status.platform_hosts.length > 0 ? status.platform_hosts.join(', ') : 'ucp.goog';
  const buttonStyle = (primary: boolean) => ({
    background: primary ? C.lime : C.bg,
    color: primary ? '#0B0B0B' : C.text,
    border: primary ? 'none' : `1px solid ${C.border}`,
    borderRadius: 2,
    fontFamily: sansFont,
    fontSize: 12,
    fontWeight: 500,
    opacity: busy ? 0.6 : 1,
  });

  return (
    <div className="space-y-3 pt-2" data-testid="ucp-platform-key" data-configured={status.configured ? 'yes' : 'no'}>
      <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
        Google&apos;s checkout signs in to your store with an access key. Create one here, then paste it into Google Merchant Center under
        {' '}<strong style={{ color: C.text, fontWeight: 500 }}>Settings → UCP integration</strong>. Sill accepts it only from Google ({hosts}).
      </div>

      {fresh !== null ? (
        <div className="space-y-2">
          <SecretShownOnce
            secret={fresh}
            kicker="Google access key"
            copyLabel="Copy access key"
            purpose="Paste this into Merchant Center. Google sends it with every checkout request, and Sill checks it before anything runs."
          />
          <button type="button" onClick={() => setFresh(null)} className="px-3 py-2" style={buttonStyle(false)} data-testid="ucp-platform-key-done">
            I&apos;ve copied it
          </button>
        </div>
      ) : null}

      {status.configured ? (
        <div className="space-y-2">
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.text }} data-testid="ucp-platform-key-status">
            Key ending in <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>…{status.hint}</span>
            {status.created_at !== null ? <span style={{ color: C.muted }}>{` · created ${shortDate(status.created_at)}`}</span> : null}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button type="button" disabled={busy} onClick={() => setConfirm('replace')} className="px-3 py-2" style={buttonStyle(false)} data-testid="ucp-platform-key-replace">
              Replace key
            </button>
            <button type="button" disabled={busy} onClick={() => setConfirm('revoke')} className="px-3 py-2" style={{ ...buttonStyle(false), color: C.coralFg }} data-testid="ucp-platform-key-revoke">
              Revoke
            </button>
          </div>
        </div>
      ) : fresh === null ? (
        <button type="button" disabled={busy} onClick={() => void create()} className="px-3 py-2" style={buttonStyle(true)} data-testid="ucp-platform-key-create">
          {busy ? 'Creating…' : 'Create access key'}
        </button>
      ) : null}

      <ConfirmDialog
        open={confirm === 'replace'}
        onClose={() => setConfirm(null)}
        title="Replace the access key?"
        body="Sill creates a new key and the current one stops working immediately. Paste the new key into Merchant Center straight away, or Google's checkout requests will be refused."
        confirmLabel="Replace key"
        confirmTone="amber"
        cancelLabel="Keep the current key"
        onConfirm={() => { setConfirm(null); void create(); }}
      />
      <ConfirmDialog
        open={confirm === 'revoke'}
        onClose={() => setConfirm(null)}
        title="Revoke the access key?"
        body="Google's checkout requests that use this key are refused from now on. Signed requests are not affected."
        confirmLabel="Revoke key"
        confirmTone="coral"
        cancelLabel="Keep the key"
        onConfirm={() => { setConfirm(null); void revoke(); }}
      />
    </div>
  );
}
